import { type FC } from "react";
import { Button, MenuItem, Stack, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Filter } from "@/partner/shared/components/Filter";
import {
  useCountryAccessesSelectQuery,
  useSetDefaultCountryAccessMutation,
} from "@/partner/entities/countryAccesses";

interface CountryAccessesFilterProps {
  onCreate: () => void;
}

export const CountryAccessesFilter: FC<CountryAccessesFilterProps> = ({
  onCreate,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { data } = useCountryAccessesSelectQuery();
  const templates = data?.countryAccesses?.nodes ?? [];
  const defaultId = templates.find((item) => item.isDefault)?.id ?? "";

  const [setDefault, { loading }] = useSetDefaultCountryAccessMutation({
    onCompleted: () => {
      enqueueSnackbar(t("countryAccesses.setDefault.success"), {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["CountryAccessesList", "CountryAccessesSelect"],
  });

  return (
    <Filter
      title={t("countryAccesses.filter.title")}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          {templates.length > 0 && (
            <TextField
              select
              size="small"
              sx={{ minWidth: 220 }}
              label={t("countryAccesses.filter.defaultLabel")}
              value={defaultId}
              disabled={loading}
              onChange={(event) =>
                setDefault({ variables: { id: event.target.value } })
              }
            >
              {templates.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.title}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            disableElevation
            onClick={onCreate}
          >
            {t("common.actions.add")}
          </Button>
        </Stack>
      }
      fields={{ search: true }}
    />
  );
};
